import { z } from 'zod';

/**
 * F16 — Reservation Engine frozen API contracts (sub-venue booking config).
 *
 * See plans/16_F16_reservation_engine.md (Task 1.2). One row per
 * `(sub_venue, sport)` carrying the slot-duration / buffer / lead-time /
 * advance-window / cancel-policy / accept-timeout configuration. Multi-sport
 * sub_venues (where `sub_venues.sport_name IS NULL`) require one config row
 * per hosted sport; single-sport sub_venues require exactly one row.
 *
 * `slotDurationMinutes` is constrained to the three founder-locked values
 * (30 / 60 / 90) so the availability grid generator can index pricing bands
 * without combinatorial explosion.
 */

export const UpsertSubVenueBookingConfigInputSchema = z.object({
  subVenueId: z.string().uuid(),
  sportName: z.string().min(1).max(40),
  slotDurationMinutes: z.union([z.literal(30), z.literal(60), z.literal(90)]),
  bufferMinutes: z.number().int().min(0).max(120),
  minLeadMinutes: z.number().int().min(0).max(43200), // up to 30 days
  maxAdvanceDays: z.number().int().min(1).max(365),
  minCancelLeadMinutes: z.number().int().min(0).max(43200),
  acceptTimeoutMinutes: z.number().int().min(5).max(10080), // up to 7 days
});
export type UpsertSubVenueBookingConfigInput = z.infer<
  typeof UpsertSubVenueBookingConfigInputSchema
>;

export type SubVenueBookingConfig = UpsertSubVenueBookingConfigInput & {
  readonly id: string;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};
